<template>
    <AxRoadPage
        :title="`${$t('shared.assets')} (${preparedAssets.length})`"
        :closable="$route.name === 'axRoadTrucks'"
        class="trucks-view"
    >
        <div class="trucks-view__controls l-stack l-gap-2">
            <div class="l-inline l-gap-1 l-center l-full-width">
                <div class="l-half-width">
                    <BaseMultiselect
                        class="trucks-view__asset-types"
                        :class="{
                            'trucks-view__asset-types--collapsed': assetTypesCollapsed,
                        }"
                        :value="selectedAssetTypes"
                        :options="assetTypesPrepared"
                        :placeholder="$t('assetType')"
                        track-by="id"
                        label="name"
                        multiple
                        @input="setSelectedAssetTypes"
                        @close="assetTypesCollapsed = true"
                        @open="assetTypesCollapsed = false"
                    />
                </div>
                <div class="l-half-width">
                    <BaseInput v-model="search" :placeholder="$t('search')">
                        <template #icon>
                            <SearchIcon width="16" height="16" />
                        </template>
                    </BaseInput>
                </div>
            </div>
            <template v-if="hasTachographAccess">
                <div
                    class="trucks-view__sort l-inline l-full-width l-center l-spread"
                    @click="showDriverSort = !showDriverSort"
                >
                    <h4 class="l-grow-1">{{ $t('moreSortOptions') }}</h4>
                    <AddIcon
                        class="trucks-view__sort-icon"
                        :class="{
                            'trucks-view__sort-icon--open': showDriverSort,
                        }"
                        height="16"
                    />
                </div>
                <transition name="slide-down">
                    <div
                        v-if="showDriverSort"
                        class="l-inline l-gap-1 l-center l-full-width"
                    >
                        <BaseMultiselect
                            v-model="driverSortSelected"
                            class="l-grow-1"
                            :options="driverSortOptions"
                            :placeholder="$t('sortBy')"
                            :allow-empty="false"
                            :custom-label="option => $t(option)"
                            :multiple="false"
                        />
                        <IconButton
                            class="trucks-view__sort-icon"
                            :class="{
                                'trucks-view__sort-icon--asc':
                                    driverSortDirection === 'asc',
                            }"
                            @click="toggleSortDirection"
                        >
                            <SortIcon />
                        </IconButton>
                    </div>
                </transition>
            </template>
        </div>
        <div>
            <AxRoadTrucksListItem
                v-for="truck in preparedAssets"
                :key="truck.id"
                :truck="truck"
                :status="truck.status"
            />
        </div>
    </AxRoadPage>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { orderBy } from 'lodash'

import AxRoadPage from '@/components/ax-road/AxRoadPage'
import AxRoadTrucksListItem from '@/components/ax-road/AxRoadTrucksListItem'
import BaseInput from '@/components/redesigned/BaseInput'
import BaseMultiselect from '@/components/redesigned/BaseMultiselect'
import SearchIcon from '@/components/icons/SearchIcon'
import { formatHelper } from '@/utils'
import IconButton from '@/components/IconButton'
import SortIcon from '@/components/icons/SortIcon'
import AddIcon from '@/components/icons/AddIcon'

const sortFields = ['noSort', ...Object.keys(formatHelper.driverTimeFormatters)]

const allowedDriverActivities = [0, 1, 2, 3, 8]

export default {
    name: 'TrucksView',
    components: {
        AxRoadPage,
        AxRoadTrucksListItem,
        BaseInput,
        BaseMultiselect,
        SearchIcon,
        IconButton,
        SortIcon,
        AddIcon,
    },
    data() {
        return {
            assetType: null,
            search: '',
            driverSortOptions: sortFields,
            driverSortDirection: 'asc',
            driverSortSelected: 'noSort',
            showDriverSort: false,
            assetTypesCollapsed: true,
        }
    },
    computed: {
        ...mapState('tracker', ['assetTypes', 'selectedAssetTypes']),
        ...mapGetters('auth', ['hasTachographAccess']),
        ...mapGetters('preferences', ['axRoadTrucksListPreferences']),
        ...mapGetters('tracker', [
            'trackersFilteredByType',
            'driversById',
            'assetTypesById',
        ]),
        assetTypesPrepared() {
            return [
                { id: 0, name: this.$t('all') },
                ...[...this.assetTypes].sort((a, b) =>
                    a.name.localeCompare(b.name)
                ),
            ]
        },
        preparedAssets() {
            const filteredAssets = this.trackersFilteredByType
                .filter(tracker => {
                    const matchesSearch = this.search
                        ? (tracker.name || '')
                              .toLowerCase()
                              .includes(this.search.toLowerCase()) ||
                          (tracker.asset_details?.name || '')
                              .toLowerCase()
                              .includes(this.search.toLowerCase()) ||
                          (tracker.asset_details?.identification || '')
                              .toLowerCase()
                              .includes(this.search.toLowerCase())
                        : true

                    const isVisibleType = this.assetTypes.some(
                        type => type.id === tracker?.asset_details?.asset_type
                    )
                    return matchesSearch && isVisibleType
                })
                .map(tracker => ({
                    ...tracker,
                    driver: this.driversById[
                        tracker?.asset_details?.sensor_data?.driver_id?.value
                    ],
                }))

            if (
                !this.driverSortSelected ||
                this.driverSortSelected === 'noSort'
            ) {
                return filteredAssets.sort((a, b) => {
                    const nameA = a?.asset_details?.name || ''
                    const nameB = b?.asset_details?.name || ''
                    return nameA.localeCompare(nameB, undefined, {
                        numeric: true,
                    })
                })
            }

            const readyAssets = filteredAssets
                .filter(({ driver, asset_details }) =>
                    // only show assets with an active driver
                    [
                        driver,
                        allowedDriverActivities.includes(
                            driver?.current_activity
                        ),
                        asset_details?.sensor_data?.driver_1_card_presence
                            ?.value === 1,
                    ].every(Boolean)
                )
                .map(asset => ({
                    ...asset,
                    status: formatHelper.driverTimeFormatters[
                        this.driverSortSelected
                    ]?.(asset.driver?.[this.driverSortSelected]),
                }))
            return orderBy(
                readyAssets,
                asset => asset.driver?.[this.driverSortSelected],
                this.driverSortDirection || 'asc'
            )
        },
    },
    watch: {
        showDriverSort(show) {
            if (!show) {
                this.driverSortSelected = 'noSort'
                this.driverSortDirection = 'asc'
            }
        },
    },
    mounted() {
        const {
            selectedAssetTypes,
            search,
            driverSortSelected,
            driverSortDirection,
            showDriverSort,
        } = this.axRoadTrucksListPreferences

        if (selectedAssetTypes) {
            this.setSelectedAssetTypes(
                [...new Set(selectedAssetTypes)]
                    .map(id => this.assetTypesById?.[id])
                    .filter(Boolean) || []
            )
        }
        this.search = search || ''
        this.driverSortSelected = driverSortSelected || 'noSort'
        this.driverSortDirection = driverSortDirection || 'asc'
        this.showDriverSort = showDriverSort || false

        if (this._preferencesUnwatch) {
            this._preferencesUnwatch()
        }
        this._preferencesUnwatch = this.$watch(
            () => [
                this.selectedAssetTypes,
                this.search,
                this.driverSortSelected,
                this.driverSortDirection,
                this.showDriverSort,
            ],
            this.updateSortPreferences,
            { deep: true }
        )
    },
    methods: {
        ...mapMutations('tracker', ['setSelectedAssetTypes']),
        ...mapActions('preferences', ['updatePreferences']),
        toggleSortDirection() {
            this.driverSortDirection =
                this.driverSortDirection === 'asc' ? 'desc' : 'asc'
        },
        updateSortPreferences() {
            this.updatePreferences({
                axRoadTrucksListPreferences: {
                    selectedAssetTypes: [
                        ...new Set(this.selectedAssetTypes.map(({ id }) => id)),
                    ],
                    search: this.search,
                    driverSortSelected: this.driverSortSelected,
                    driverSortDirection: this.driverSortDirection,
                    showDriverSort: this.showDriverSort,
                },
            })
        },
    },
}
</script>

<i18n>
{
    "en": {
        "search": "Search...",
        "assetType": "Asset Type",
        "all": "All",
        "moreSortOptions": "Sort by driver times",
        "sortBy": "Sort by",
        "noSort": "Don't sort",
        "work_start_time": "Start time",
        "cumulative_break_time": "Break total",
        "current_daily_driving_time": "Total driving time so far",
        "work_time_total": "Total working time so far",
        "remaining_time_until_next_break": "Time to next break",
        "remaining_time_current_shift": "Time to daily limit",
        "time_left_until_new_daily_rest_period": "Working time to daily limit"
    },
    "de": {
        "search": "Suchen...",
        "assetType": "Asset Typ",
        "all": "Alle",
        "moreSortOptions": "Zusätzliche Fahrzeitensortierung",
        "sortBy": "Sortieren nach",
        "noSort": "Nicht sortieren",
        "work_start_time": "Arbeitsbeginn",
        "cumulative_break_time": "Pause Total",
        "current_daily_driving_time": "Total Lenkzeit bisher",
        "work_time_total": "Total Arbeitszeit bisher",
        "remaining_time_until_next_break": "LZ bis zur n. Pause",
        "remaining_time_current_shift": "LZ bis zum Tageslimit",
        "time_left_until_new_daily_rest_period": "Arbeitszeit bis Tageslimit"
    },
    "fr": {
        "search": "Rechercher...",
        "assetType": "Type d'actif",
        "all": "Tous",
        "moreSortOptions": "Trier par les temps du conducteur",
        "sortBy": "Trier par",
        "noSort": "Ne pas trier",
        "work_start_time": "Heure de début",
        "cumulative_break_time": "Pause totale",
        "current_daily_driving_time": "Temps de conduite total jusqu'à présent",
        "work_time_total": "Temps de travail total jusqu'à présent",
        "remaining_time_until_next_break": "Temps avant la prochaine pause",
        "remaining_time_current_shift": "Temps avant la limite quotidienne",
        "time_left_until_new_daily_rest_period": "Temps de travail avant la limite quotidienne"
    },
    "it": {
        "search": "Ricerca...",
        "assetType": "Tipo di asset",
        "all": "Tutti",
        "moreSortOptions": "Ordina per i tempi del conducente",
        "sortBy": "Ordina per",
        "noSort": "Non ordinare",
        "work_start_time": "Ora di inizio",
        "cumulative_break_time": "Pausa totale",
        "current_daily_driving_time": "Tempo di guida totale finora",
        "work_time_total": "Tempo di lavoro totale finora",
        "remaining_time_until_next_break": "Tempo alla prossima pausa",
        "remaining_time_current_shift": "Tempo al limite giornaliero",
        "time_left_until_new_daily_rest_period": "Tempo di lavoro al limite giornaliero"
    }
}
</i18n>

<style lang="scss" scoped>
.trucks-view {
    &__controls {
        gap: 12px;
        padding-bottom: 12px;
        border-bottom: $style-border;
    }
    &__asset-types {
        &--collapsed {
            max-height: 40px;
            overflow: hidden;
        }
    }

    &__sort {
        cursor: pointer;
        color: $color-gray-darker;
    }

    &__sort-icon {
        transition: all 0.1s ease-in-out;
        &--asc {
            transform: scaleY(-1);
        }
        &--open {
            transform: rotate(45deg);
        }
    }
}

.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease;
}
.slide-down-enter,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-20%);
    margin: -12px 0 0 0;
    padding: 0;
    max-height: 0;
    overflow: hidden;
}
.slide-down-enter-to,
.slide-down-leave {
    opacity: 1;
    transform: translateY(0);
    max-height: 200px;
    overflow: hidden;
}
</style>
